import { doc, onSnapshot } from 'firebase/firestore'
import { create } from 'zustand'
import { db } from '@/lib/firebase'
import { findHouseholdByEmail, joinHousehold } from '@/data/household'
import type { HouseholdDoc, UserDoc } from '@/types'
import type { AuthUser } from './auth'

export type Onboarding = 'none' | 'categories' | 'name'

interface HouseholdState {
  /** loading until the user document and the household have both arrived */
  status: 'loading' | 'none' | 'ready'
  hid: string | null
  household: HouseholdDoc | null
  userDoc: UserDoc | null
  /** A first-run step still to show before landing on the Plan tab. */
  onboarding: Onboarding
  error: string | null
}

export const useHousehold = create<HouseholdState>()(() => ({
  status: 'loading',
  hid: null,
  household: null,
  userDoc: null,
  onboarding: 'none',
  error: null,
}))

export function setOnboarding(step: Onboarding): void {
  useHousehold.setState({ onboarding: step })
}

/**
 * Watches users/{uid}. With a householdId, watches that household. Without one,
 * looks for a household whose allowlist holds the person's email and joins it;
 * failing that, reports `none` so the welcome screen shows.
 */
export function startHouseholdListener(user: AuthUser): () => void {
  let stopHousehold: (() => void) | null = null
  let watchingHid: string | null = null
  let lookedUp = false

  const watchHousehold = (hid: string) => {
    if (watchingHid === hid) return
    stopHousehold?.()
    watchingHid = hid
    stopHousehold = onSnapshot(
      doc(db, 'households', hid),
      (snap) => {
        if (!snap.exists()) {
          useHousehold.setState({ status: 'none', hid: null, household: null })
          return
        }
        useHousehold.setState({ status: 'ready', hid, household: snap.data() as HouseholdDoc, error: null })
      },
      (err) => useHousehold.setState({ status: 'none', error: err.message }),
    )
  }

  const lookUp = async () => {
    if (lookedUp) return
    lookedUp = true
    try {
      const hid = await findHouseholdByEmail(user.email)
      if (!hid) {
        useHousehold.setState({ status: 'none', hid: null, household: null })
        return
      }
      await joinHousehold(hid, user.uid, firstName(user.displayName))
      useHousehold.setState({ onboarding: 'name' })
      // the users/{uid} listener now sees householdId and watches the household
    } catch (err) {
      useHousehold.setState({ status: 'none', error: (err as Error).message })
    }
  }

  const stopUser = onSnapshot(
    doc(db, 'users', user.uid),
    (snap) => {
      const data = snap.exists() ? (snap.data() as UserDoc) : null
      useHousehold.setState({ userDoc: data })
      if (data?.householdId) {
        // Right after creating or joining, this snapshot comes from the local
        // write before the server has the household document. Wait for the
        // acknowledged one, or the household listener would be refused.
        if (!snap.metadata.hasPendingWrites) watchHousehold(data.householdId)
      } else if (!snap.metadata.fromCache || !snap.exists()) {
        void lookUp()
      }
    },
    (err) => useHousehold.setState({ status: 'none', error: err.message }),
  )

  return () => {
    stopUser()
    stopHousehold?.()
    useHousehold.setState({ status: 'loading', hid: null, household: null, userDoc: null, onboarding: 'none', error: null })
  }
}

export function firstName(full: string): string {
  return full.trim().split(/\s+/)[0] ?? ''
}
